import * as d3 from "d3";
import store from "../../../store/store";
import { CrainData } from "./shared/CrainD3Vis";
import { DocUniverse } from "./shared/DocUniverse.js";

export default class {
  svg;
  documentUniverse;
  timeSlider;
  showContour = false;
  showFields = false;
  showFieldsNames = false;
  showArrows = false;
  showThemes = false;
  showLabels = false;
  showDocuments = false;
  showNonMatchingDocuments = true;
  showOverlappingDocuments = true;
  showRings = false;
  showRingsNoMatch = false;
  startYear;
  recentYear;
  stopYear;
  isolateFields;
  minimumConnections;
  cutoff;
  associations;
  ring;
  startQuery;

  constructor(svg) {
    this.svg = svg;

    const year = new Date().getFullYear();

    this.startQuery = {
      xlayers: [
        {
          layers: [["Fields", 0]]
        }
      ],
      ylayers: null,
      zlayers: [null],
      subset_filters: [],
      recentyear: year - 5,
      startyear: year - 20,
      stopyear: year,
      postprocessing: ["ydocuments", "recentdocuments", "documents"]
    };
  }

  init(reload = false, onlyMap, onlyTable) {
    //document.getElementById("load-div-docuni").classList.add("show");
    store.dispatch('loadAnimStart');
    store.dispatch('saveLoadingStep', "Drawing Doc Universe");
    let crainData, crainDataTable, crainDataThemes;
    let reset = false;
    let ring = store.state.field.fieldRings;
    let tabletop = store.state.field.fieldTableTop;
    if (Object.keys(store.state.visualization.visData).length === 0 || reload) {
      // Query for Fields Map
      this.startQuery.ylayers = ring;
      this.startQuery.startyear = this.startYear;
      this.startQuery.recentyear = this.recentYear;
      // this.startQuery.stopyear = this.stopYear;
      if (store.state.filter.filterFocused) {
        this.startQuery.subset_filters =
          [store.state.filter.filterFocused.rawquery];
      } else {
        this.startQuery.subset_filters = [];
      }
      crainData = new CrainData(JSON.stringify(this.startQuery));

      // Query for Themes of Fields Map
      let themeq = { ...this.startQuery };
      themeq.ylayers = null;
      themeq.xlayers = this.associations;
      themeq.startyear = this.startYear;
      themeq.postprocessing = [];
      crainDataThemes = new CrainData(JSON.stringify(themeq));

      // Query for Fields Table
      if (tabletop) {
        let recencyQuery = { ...this.startQuery };
        recencyQuery.ylayers = tabletop;
        recencyQuery.startyear = this.startYear;
        recencyQuery.postprocessing = [];
        crainDataTable = new CrainData(JSON.stringify(recencyQuery));
      } else {
        crainDataTable = null;
      }

      let cds;
      if (!(onlyMap || onlyTable)) {
        cds = [crainData, crainDataThemes, crainDataTable];
      } else if (onlyMap) {
        cds = [crainData];
        crainDataThemes = store.state.visualization.visData.crainDataThemes;
        crainDataTable = store.state.visualization.visData.crainDataTable;
      } else if (onlyTable) {
        cds = [crainDataTable];
        crainData = store.state.visualization.visData.crainDataMap;
        crainDataThemes = store.state.visualization.visData.crainDataThemes;
      }

      Promise.all(
        cds.filter(x => x instanceof CrainData).map(cd => cd.load())
      ).then(() => {
        store.dispatch("saveVisData", {
          crainDataMap: crainData,
          crainDataThemes: crainDataThemes,
          crainDataTable: crainDataTable
        });
        this.prepDrawUniverse(
          reset,
          crainData,
          crainDataThemes,
          crainDataTable
        );
      });
    } else {
      // Reuse previous data
      crainData = store.state.visualization.visData.crainDataMap;
      crainDataThemes = store.state.visualization.visData.crainDataThemes;
      crainDataTable = store.state.visualization.visData.crainDataTable;

      this.prepDrawUniverse(reset, crainData, crainDataThemes, crainDataTable);
    }
  }
  prepDrawUniverse(reset, crainData, crainDataThemes, crainDataTable) {
    if (this.documentUniverse === undefined) {
      this.documentUniverse = new DocUniverse(
        d3.select(this.svg),
        crainData,
        crainDataThemes
      );
      reset = true;
    } else {
      this.documentUniverse.crainData = crainData;
      this.documentUniverse.crainDataTable = crainDataTable;
      this.documentUniverse.crainDataThemes = crainDataThemes;
    }
    this.draw(reset, true);
  }
  setTimeSlider(value) {
    this.timeSlider = value;
  }
  setShowContour(value) {
    this.showContour = value;
  }
  setShowFields(value) {
    this.showFields = value;
  }
  setShowFieldsNames(value) {
    this.showFieldsNames = value;
  }
  setShowArrows(value) {
    this.showArrows = value;
  }
  setShowThemes(value) {
    this.showThemes = value;
  }
  setShowLabels(value) {
    this.showLabels = value;
  }
  setShowDocuments(value) {
    this.showDocuments = value;
  }
  setShowNonMatchingDocuments(value) {
    this.showNonMatchingDocuments = value;
  }
  setShowOverlappingDocuments(value) {
    this.showOverlappingDocuments = value;
  }
  setShowRings(value) {
    this.showRings = value;
  }
  setShowRingsNoMatch(value) {
    this.showRingsNoMatch = value;
  }
  setStartYear(value) {
    this.startYear = parseInt(value);
  }
  setRecentYear(value) {
    this.recentYear = parseInt(value);
  }
  setStopYear(value) {
    this.stopYear = parseInt(value);
  }
  setIsolateFields(value) {
    this.isolateFields = value;
  }
  setMinimumConnections(value) {
    this.minimumConnections = parseInt(value);
  }
  setCutoff(value) {
    this.cutoff = value;
  }
  setAssociations(value) {
    this.associations = value;
  }
  draw(reset = false, newData = false) {
    if (!this.documentUniverse) {
      console.warn("Visualization not initialized yet");
      return;
    }
    let config = {
      showContour: this.showContour,
      showFields: this.showFields,
      showFieldsNames: this.showFieldsNames,
      showArrows: this.showArrows,
      showThemes: this.showThemes,
      showLabels: this.showLabels,
      showDocs: this.showDocuments,
      showNoMatch: this.showNonMatchingDocuments,
      showOverlap: this.showOverlappingDocuments,
      showRings: this.showRings,
      showRingsNoMatch: this.showRingsNoMatch,
      isolate: this.isolateFields,
      cutoff: this.cutoff,
      minConnections: this.minimumConnections
    };

    const resetZoom = false;
    this.documentUniverse.update(reset, newData, resetZoom, config);
  }
  destroy() {
    if (!this.documentUniverse) {
      return;
    }
    this.documentUniverse.destroy();
  }
}
